import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../hooks/useTranslation';
import { submissionsService } from '../../services/submissions.service';
import LoadingState from '../../components/ui/LoadingState';

const { FiCheck, FiX, FiEye, FiMessageSquare, FiFilter, FiRefreshCw } = FiIcons;

function ModerationQueue() {
  const { t } = useTranslation();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState(null);
  const [filter, setFilter] = useState('all'); // all, submitted, approved, rejected

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const data = await submissionsService.getModerationQueue();
      setSubmissions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await submissionsService.updateSubmissionStatus(id, status);
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      if (selectedSub?.id === id) setSelectedSub(null);
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const filtered = submissions.filter(s => filter === 'all' || s.status === filter);

  if (loading) return <LoadingState message="Loading moderation queue..." />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Moderation Queue</h1>
          <p className="text-slate-500">Review and approve volunteer contributions</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-white rounded-lg border border-slate-200 p-1">
            {['all', 'submitted', 'approved', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  filter === f ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
          <button 
            onClick={loadSubmissions}
            className="p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* List */}
        <div className="lg:col-span-2 space-y-4">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-slate-200">
              <SafeIcon icon={FiFilter} className="text-4xl text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No submissions found matching your filter.</p>
            </div>
          ) : (
            filtered.map((sub, idx) => (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`group bg-white p-5 rounded-2xl border transition-all cursor-pointer ${
                  selectedSub?.id === sub.id ? 'border-blue-500 ring-2 ring-blue-50' : 'border-slate-100 hover:border-slate-300'
                }`}
                onClick={() => setSelectedSub(sub)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      sub.type === 'resource' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                    }`}>
                      <SafeIcon icon={sub.type === 'resource' ? FiFileText : FiMessageSquare} className="text-xl" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {sub.content?.title || sub.content?.name || 'Untitled Submission'}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        By {sub.hahub_profiles?.first_name} {sub.hahub_profiles?.last_name} • {new Date(sub.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          sub.status === 'submitted' ? 'bg-orange-100 text-orange-700' :
                          sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {sub.status}
                        </span>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{sub.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleUpdateStatus(sub.id, 'approved'); }}
                      className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                      title="Approve"
                    >
                      <SafeIcon icon={FiCheck} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleUpdateStatus(sub.id, 'rejected'); }}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Reject"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Sidebar Detail */}
        <div className="lg:col-span-1">
          <AnimatePresence mode="wait">
            {selectedSub ? (
              <motion.div
                key={selectedSub.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm sticky top-8 overflow-hidden"
              >
                <div className="bg-slate-50 p-6 border-b border-slate-100">
                  <h2 className="font-bold text-slate-900">Submission Details</h2>
                  <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest font-bold">{selectedSub.id}</p>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Content Payload</label>
                    <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-xl overflow-x-auto max-h-60 scrollbar-hide">
                      {JSON.stringify(selectedSub.content, null, 2)}
                    </pre>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Admin Actions</label>
                    <div className="flex flex-col gap-2">
                      <button 
                        onClick={() => handleUpdateStatus(selectedSub.id, 'approved')}
                        disabled={selectedSub.status === 'approved'}
                        className="w-full py-3 rounded-xl bg-green-600 text-white font-bold text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiCheck} /> Approve & Publish
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(selectedSub.id, 'rejected')}
                        disabled={selectedSub.status === 'rejected'}
                        className="w-full py-3 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiX} /> Reject Submission
                      </button>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-50 text-center">
                    <p className="text-xs text-slate-400">
                      Submitted on {new Date(selectedSub.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400 italic text-sm">
                Select a submission to view details
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default ModerationQueue;